// frontend/src/components/TutorFeedbackTab.js
import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Modal, Form, Input, Select, List, Typography, Space, Timeline, Alert, Skeleton } from 'antd';
import { MessageOutlined, EditOutlined, CalendarOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import { getStudentFeedback, submitTutorFeedback } from '../services/feedbackService';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import dayjs from 'dayjs';

const { Text, Title, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;

const TutorFeedbackTab = ({ studentId, isTutor }) => {
    const { user } = useAuth();
    const { message: notify } = useNotification();
    
    const [feedbacks, setFeedbacks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalVisible, setModalVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();
    const [editingId, setEditingId] = useState(null);
    const [settings, setSettings] = useState(null);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await api.get('/settings');
                setSettings(response.data);
            } catch (err) {
                console.error("Failed to load settings in TutorFeedbackTab:", err);
            }
        };
        fetchSettings();
    }, []);

    const fetchFeedbacks = useCallback(async () => {
        setLoading(true);
        try {
            const data = await getStudentFeedback(studentId);
            setFeedbacks(data);
        } catch (error) {
            console.error('Error fetching student feedback:', error);
            notify.error('Failed to load tutor feedback.');
        } finally {
            setLoading(false);
        }
    }, [studentId, notify]);

    useEffect(() => {
        fetchFeedbacks();
    }, [fetchFeedbacks]);

    const handleOpenModal = (existingFeedback = null) => {
        if (existingFeedback) {
            setEditingId(existingFeedback.id);
            form.setFieldsValue({
                term: existingFeedback.term,
                feedback: existingFeedback.feedback
            });
        } else {
            setEditingId(null);
            form.resetFields();
        }
        setModalVisible(true);
    };

    const handleCancel = () => {
        setModalVisible(false);
        form.resetFields();
    };

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            await submitTutorFeedback(studentId, values.term, values.feedback);
            notify.success(editingId ? 'Feedback updated successfully!' : 'Feedback submitted successfully!');
            setModalVisible(false);
            form.resetFields();
            fetchFeedbacks();
        } catch (error) {
            console.error('Error submitting feedback:', error);
            notify.error(error.response?.data?.error || 'Failed to submit feedback.');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <Card style={{ borderRadius: '12px' }}>
                <Skeleton active avatar paragraph={{ rows: 4 }} />
            </Card>
        );
    }

    const isAdmin = user.role === 'principal' || user.role === 'super_admin';

    return (
        <div style={{ padding: '8px 0' }}>
            <Card 
                title={
                    <Space>
                        <MessageOutlined style={{ color: '#1890ff' }} />
                        <span>Tutor Assessment & Feedback</span>
                    </Space>
                }
                extra={
                    isTutor && (user.role !== 'teacher' || settings?.allow_teacher_feedback_entry !== false) && (
                        <Button 
                            type="primary" 
                            icon={<PlusOutlined />} 
                            onClick={() => handleOpenModal()}
                            style={{ borderRadius: '6px' }}
                        >
                            Add/Update Feedback
                        </Button>
                    )
                }
                style={{ 
                    borderRadius: '12px', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                    border: '1px solid #f0f0f0' 
                }}
            >
                {feedbacks.length === 0 ? (
                    <Alert
                        message="No feedback recorded yet."
                        description={isTutor ? "As the assigned tutor, you can assess the student's status and add your feedback for each term." : "No feedback has been recorded for this student for the current academic year."}
                        type="info"
                        showIcon
                    />
                ) : (
                    <Timeline mode="left" style={{ marginTop: '20px' }}>
                        {feedbacks.map((item) => (
                            <Timeline.Item 
                                key={item.id}
                                color="blue"
                                label={
                                    <Space direction="vertical" size={0}>
                                        <Text strong style={{ fontSize: '15px' }}>{item.term}</Text>
                                        <Text type="secondary" style={{ fontSize: '12px' }}>
                                            <CalendarOutlined /> {dayjs(item.updated_at).format('DD MMM YYYY')}
                                        </Text>
                                    </Space>
                                }
                            >
                                <Card 
                                    style={{ 
                                        borderRadius: '8px', 
                                        background: '#fafafa',
                                        border: '1px solid #f0f0f0',
                                        marginBottom: '10px'
                                    }}
                                    bodyStyle={{ padding: '16px' }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <Space direction="vertical" style={{ width: '90%' }}>
                                            <Paragraph style={{ margin: 0, fontSize: '14px', whiteSpace: 'pre-wrap' }}>
                                                {item.feedback}
                                            </Paragraph>
                                            <Text type="secondary" style={{ fontSize: '12px', display: 'block', marginTop: '8px' }}>
                                                <UserOutlined /> Tutor: <Text strong>{item.tutor_name}</Text>
                                            </Text>
                                        </Space>
                                        {isTutor && item.teacher_id === user.id && (user.role !== 'teacher' || settings?.allow_teacher_feedback_entry !== false) && (
                                            <Button 
                                                type="text" 
                                                icon={<EditOutlined />} 
                                                onClick={() => handleOpenModal(item)}
                                                title="Edit feedback"
                                            />
                                        )}
                                    </div>
                                </Card>
                            </Timeline.Item>
                        ))}
                    </Timeline>
                )}
            </Card>

            {/* Modal for submission/update */}
            <Modal
                title={editingId ? "Update Tutor Feedback" : "Submit Tutor Feedback"}
                open={modalVisible}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Cancel
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        loading={submitting} 
                        onClick={() => form.submit()}
                    >
                        Save Feedback
                    </Button>
                ]}
                destroyOnClose
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                    style={{ marginTop: '16px' }}
                >
                    <Form.Item
                        name="term"
                        label="Academic Term"
                        rules={[{ required: true, message: 'Please select an academic term' }]}
                    >
                        <Select placeholder="Select term">
                            <Option value="Term 1">Term 1</Option>
                            <Option value="Term 2">Term 2</Option>
                            <Option value="Term 3">Term 3</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="feedback"
                        label="Status Assessment & Feedback"
                        rules={[
                            { required: true, message: 'Please enter feedback text' },
                            { min: 10, message: 'Feedback should be at least 10 characters long' }
                        ]}
                    >
                        <TextArea 
                            rows={6} 
                            placeholder="Describe the student's status, progress, concerns, or follow-up details..." 
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default TutorFeedbackTab;
